/**
 * Dashboard Hooks
 *
 * React hooks for fetching dashboard statistics.
 */

import { useState, useEffect, useCallback } from 'react';
import { getDashboardStats } from '../api';
import type { DashboardStats } from '../types';
import { ApiClientError } from '../api';

// ============================================
// Hook Types
// ============================================

interface UseDashboardResult {
  data: DashboardStats | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// ============================================
// useDashboard Hook
// ============================================

export function useDashboard(): UseDashboardResult {
  const [data, setData] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getDashboardStats();
      setData(response);
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Failed to fetch dashboard stats';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refetch: fetchData,
  };
}
